import { AxiosError } from "axios";
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../app/store";
import { SIDEBAR_GRAPH_WIDTH } from "../../../constants/constants";
import { DataNode } from "../../../interface/Trends";
import {
  getLevel2Data,
  showChart,
} from "../../../utils/trendSidebarGraphUtils/colorAnalysisUtils";
import { syncColorSelectionForTrendCurves } from "../../../utils/trendSidebarGraphUtils/trendCurvesUtils";
import CollapseComponent from "../TrendsSnapshot/CollapseComponent";
import ColorAnalysisTable from "./ColorAnalysisTable";

type ColorAnalysisProps = {
  width?: number;
  data: DataNode;
  colorForLevel2?: string;
  setColorForLevel2?: React.Dispatch<React.SetStateAction<string>>;
  colorForLevel3?: string;
  setColorForLevel3?: React.Dispatch<React.SetStateAction<string>>;
  dataForLevel2: DataNode | null;
  setDataForLevel2: React.Dispatch<React.SetStateAction<DataNode | null>>;
  trendFilterType: string;
  expandedView?: boolean;
};

const ColorAnalysisGraph: React.FC<ColorAnalysisProps> = ({
  width = SIDEBAR_GRAPH_WIDTH,
  data,
  colorForLevel2,
  setColorForLevel2,
  dataForLevel2,
  setDataForLevel2,
  trendFilterType,
  expandedView = false,
}) => {
  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef3 = useRef<HTMLDivElement>(null);
  const [l2Loading, setL2Loading] = useState(false);
  const firstMount = useRef(true);

  const user = useSelector((state: RootState) => state.user.userInfo);

  const [l1Open, setL1Open] = useState(true);
  const [l2Open, setL2Open] = useState(true);

  const toggleL1Open = () => setL1Open(prev => !prev);
  const toggleL2Open = () => setL2Open(prev => !prev);

  const collapseLevel2 = () => {
    if (setColorForLevel2) {
      setColorForLevel2("");
    }
    setL2Open(false);
    setDataForLevel2(null);
  };

  const fetchL2ColorAnalysisData = async (primary_colour: string) => {
    try {
      setL2Loading(true);
      const response = await getLevel2Data(user, primary_colour);
      if (response && chartRef3.current) {
        const data = response;
        setDataForLevel2(data as DataNode);
        showChart(
          data as DataNode,
          chartRef3.current,
          "LEVEL2",
          width,
          expandedView
        );
        if (!firstMount.current) {
          setTimeout(() => {
            chartRef3.current?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }
      }
      setL2Open(true);
    } catch (error) {
      console.error("Error Fetching level 2 data", error);
      if ((error as AxiosError)?.response?.status !== 400) {
        toast.error("Error Fetching level 2 data");
      }
    } finally {
      setL2Loading(false);
    }
  };

  useEffect(() => {
    if (chartRef1.current) {
      showChart(
        data,
        chartRef1.current,
        "LEVEL1",
        width,
        expandedView,
        setColorForLevel2
      );
    }
  }, []);

  useEffect(() => {
    setDataForLevel2(null);
    syncColorSelectionForTrendCurves(colorForLevel2 ?? "");
    if (chartRef3.current && colorForLevel2) {
      fetchL2ColorAnalysisData(colorForLevel2?.split("#")[1]).then(() => {
        firstMount.current = false;
      });
      if (!firstMount.current) {
        chartRef3.current?.scrollIntoView({ behavior: "smooth" });
      }
      setL2Open(true);
    }
    if (!(chartRef3.current && colorForLevel2)) {
      firstMount.current = false;
    }
  }, [colorForLevel2]);

  return (
    <div className="space-y-4">
      {!expandedView && data && (
        <CollapseComponent
          open={l1Open}
          toggle={toggleL1Open}
          text="L1 Analysis"
          snapshot={false}
          expandedView={true}
        />
      )}
      <div
        ref={chartRef1}
        id="LEVEL1"
        className={` ${!l1Open ? "hidden" : "block"}`}
      />
      {expandedView && <ColorAnalysisTable data1={data} tableNo={1} />}
      {!expandedView && dataForLevel2 && (
        <CollapseComponent
          open={l2Open}
          toggle={toggleL2Open}
          text="L2 Analysis"
          snapshot={false}
          onClear={collapseLevel2}
        />
      )}
      <div
        className={`${l2Loading || !l2Open || !dataForLevel2 ? "hidden" : "block"} relative`}
      >
        <div ref={chartRef3} id="LEVEL2" />
        <button
          className={`text-red-700 absolute top-0 right-0 mr-4 mt-4 text-lg ${
            expandedView ? "block" : "hidden"
          }`}
          onClick={collapseLevel2}
        >
          Clear
        </button>
      </div>
      <div
        className={`text-3xl font-bold py-20 text-center ${l2Loading ? "block" : "hidden"}`}
      >
        <Skeleton height={width} width={width} circle={true} />
      </div>
      {expandedView && <ColorAnalysisTable data2={dataForLevel2} tableNo={2} />}
    </div>
  );
};

export default ColorAnalysisGraph;
